import { serve } from '@hono/node-server'

import { Command } from 'commander'
import { Hono } from 'hono'
import { stream } from 'hono/streaming'
import as from 'ansis'

import { watchFile } from '@/helpers'
import { output } from '@/helpers/std-out'

import type { OpenAPI } from '@scalar/openapi-types'
import { printSpecificationBanner } from '@/domains/document/helpers'
import { loadOpenApiFile } from '@/domains/document/helpers'

export function ServeCommand() {
  const cmd = new Command('serve')

  cmd.description('Serve an API Reference from an OpenAPI file')
  cmd.argument('[file|url]', 'OpenAPI file or URL to show the reference for')
  cmd.option('-w, --watch', 'watch the file for changes')
  cmd.option('-o, --once', 'run the server only once and exit after that')
  cmd.option(
    '-p, --port <port>',
    'set the HTTP port for the API reference server',
  )
  cmd.action(
    async (
      inputArgument: string,
      { watch, once, port }: { watch?: boolean; once?: boolean; port?: number },
    ) => {
      if (!inputArgument) {
        return output
          .error()
          .title('Invalid argument')
          .message('Please provide a file path')
          .exit('error')
      }
      const result = await loadOpenApiFile(inputArgument)

      if (!result.valid) {
        return
      }

      let { specification } = result

      printSpecificationBanner({
        version: result.version,
        schema: result.schema,
      })

      if (
        specification?.paths === undefined ||
        Object.keys(specification?.paths).length === 0
      ) {
        output
          .warn()
          .title('Could not find any paths in the OpenAPI file.')
          .print()
      }

      const app = new Hono()

      app.get('/', (c) => {
        return c.html(getHtmlDocument(specification ?? {}, watch))
      })

      app.use('/__watcher', async (c, next) => {
        c.header('Content-Type', 'text/event-stream')
        c.header('Cache-Control', 'no-cache')
        c.header('Connection', 'keep-alive')
        await next()
      })

      app.get('/__watcher', (c) => {
        return stream(c, async (s) => {
          // watch file for changes
          if (watch) {
            const subscription = await watchFile(inputArgument, async () => {
              const newResult = await loadOpenApiFile(inputArgument)
              const specificationHasChanged =
                newResult?.specification &&
                JSON.stringify(specification) !==
                  JSON.stringify(newResult.specification)

              if (specificationHasChanged) {
                output.info().title(as.grey('OpenAPI file modified')).print()

                printSpecificationBanner({
                  version: newResult.version,
                  schema: newResult.schema,
                })

                specification = newResult.specification

                s.write('data: file modified\n\n')
              }
            })

            if (subscription) {
              s.onAbort(() => subscription.unsubscribe())
            }
          }

          while (true) {
            await new Promise((resolve) => setTimeout(resolve, 100))
          }
        })
      })

      const server = serve(
        {
          fetch: app.fetch,
          port: port ?? 3000,
        },
        (info) => {
          output
            .info()
            .line(
              `${as.green.bold('➜ API Reference Server')} ${as.white(
                'listening on',
              )} ${as.cyan(`http://localhost:${info.port}`)}`,
            )
            .print()
        },
      )

      // Exit after the first run
      if (once) {
        setTimeout(() => {
          server.close()
        }, 2000)
      }
    },
  )

  return cmd
}

/** Load the CDN references via a simple HTML document */
export const getHtmlDocument = (
  specification: OpenAPI.Document,
  watch = false,
) => {
  return `<!doctype html>
    <html>
      <head>
        <title>Scalar API Reference</title>
        <meta charset="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1" />
        <style>
          body {
            margin: 0;
          }
        </style>
        ${
          watch
            ? `
              <script>
                const evtSource = new EventSource('__watcher');
                evtSource.onmessage = (event) => {
                  console.log(\`message: \${event.data}\`);
                  window.location.reload();
                };
              </script>
            `
            : ''
        }
      </head>
      <body>
        <script
          id="api-reference"
          type="application/json"
          data-proxy-url="https://proxy.scalar.com">${JSON.stringify(specification)}</script>
        <script src="https://cdn.jsdelivr.net/npm/@scalar/api-reference"></script>
      </body>
    </html>`
}
